import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { IMAGES } from "@/lib/api";

const PUBLIC_LINKS = [
  { path: "/vad-ar-emotionell-frihet", label: "Metoden" },
  { path: "/boken", label: "Boken" },
  { path: "/om-mig", label: "Om mig" },
  { path: "/recensioner", label: "Recensioner" },
  { path: "/vanliga-fragor", label: "FAQ" },
];

const DASHBOARD_LINKS = [
  { path: "/dashboard", label: "Hem" },
  { path: "/tester", label: "Tester" },
  { path: "/ovningar", label: "Övningar" },
  { path: "/dagbok", label: "Dagbok" },
  { path: "/progress", label: "Min resa" },
  { path: "/boken", label: "Boken" },
];

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPaid = user && user.has_paid;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const go = (path) => { navigate(path); setIsOpen(false); };
  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/95 backdrop-blur-md border-b border-[#E8E0DB]/60 shadow-sm shadow-black/[0.02]' : 'bg-transparent'
    }`} data-testid="main-navigation">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">

          {/* Logo */}
          <a href={isPaid ? "/dashboard" : "/"} className="flex items-center gap-2 sm:gap-2.5 group" data-testid="nav-logo">
            <img src={IMAGES.logo} alt="Emotionell Frihet" className="w-8 h-8 sm:w-9 sm:h-9 object-contain" />
            <span className="heading-font text-lg sm:text-xl font-medium text-[#111111] group-hover:text-[#A0655A] transition-colors">
              Emotionell Frihet<sup className="text-[8px] align-super ml-0.5">™</sup>
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {isPaid ? (
              <>
                {DASHBOARD_LINKS.map(link => (
                  <button key={link.path} onClick={() => go(link.path)}
                    className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      isActive(link.path)
                        ? 'text-[#A0655A] bg-[#A0655A]/[0.06] font-medium'
                        : 'text-[#111111]/55 hover:text-[#A0655A] hover:bg-[#A0655A]/[0.04]'
                    }`}
                    data-testid={`nav-link-${link.path.slice(1)}`}>
                    {link.label}
                  </button>
                ))}
                <div className="w-px h-5 bg-[#EBE3DF] mx-2" />
                {user.role === 'admin' && (
                  <button onClick={() => go('/admin')}
                    className={`px-2.5 py-1.5 rounded-lg text-xs transition-all ${
                      isActive('/admin') ? 'text-[#A0655A] bg-[#A0655A]/[0.06] font-medium' : 'text-[#111111]/40 hover:text-[#A0655A]'
                    }`}
                    data-testid="nav-admin-btn">
                    Admin
                  </button>
                )}
                <span className="text-xs text-[#111111]/35 mr-1">{user.name}</span>
                <Button onClick={handleLogout} variant="ghost" size="sm"
                  className="text-[#111111]/40 hover:text-[#A0655A] hover:bg-[#A0655A]/[0.04] rounded-lg h-8 px-2"
                  data-testid="nav-logout-btn">
                  <LogOut size={15} />
                </Button>
              </>
            ) : (
              <>
                {PUBLIC_LINKS.map(link => (
                  <button key={link.path} onClick={() => go(link.path)}
                    className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      isActive(link.path)
                        ? 'text-[#A0655A] bg-[#A0655A]/[0.06] font-medium'
                        : 'text-[#111111]/55 hover:text-[#A0655A] hover:bg-[#A0655A]/[0.04]'
                    }`}
                    data-testid={`nav-link-${link.path.slice(1)}`}>
                    {link.label}
                  </button>
                ))}
                <div className="w-px h-5 bg-[#EBE3DF] mx-2" />
                {user ? (
                  <div className="flex items-center gap-2">
                    <Button onClick={() => go('/betalning')}
                      className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full text-sm h-9 px-5"
                      data-testid="nav-get-access-btn">
                      Få tillgång
                    </Button>
                    <Button onClick={handleLogout} variant="ghost" size="sm"
                      className="text-[#111111]/40 hover:text-[#A0655A] rounded-lg h-8 px-2"
                      data-testid="nav-logout-btn">
                      <LogOut size={15} />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button onClick={() => go('/logga-in')}
                      className="text-sm text-[#111111]/55 hover:text-[#A0655A] px-3 py-2 rounded-lg transition-colors"
                      data-testid="nav-login-btn">
                      Logga in
                    </button>
                    <Button onClick={() => go('/registrera')}
                      className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full text-sm h-9 px-5"
                      data-testid="nav-register-btn">
                      Kom igång
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-[#A0655A]/[0.04] transition-colors"
            data-testid="mobile-hamburger">
            {isOpen ? <X size={22} className="text-[#111111]/70" /> : <Menu size={22} className="text-[#111111]/70" />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="md:hidden overflow-hidden"
            >
              <div className="pb-6 pt-2 bg-white">
                <div className="flex flex-col">
                  {isPaid ? (
                    <>
                      {DASHBOARD_LINKS.map(link => (
                        <button key={link.path} onClick={() => go(link.path)}
                          className={`text-left py-3 px-3 rounded-lg text-sm transition-colors ${
                            isActive(link.path)
                              ? 'text-[#A0655A] bg-[#A0655A]/[0.06] font-medium'
                              : 'text-[#111111]/65 hover:text-[#A0655A]'
                          }`}
                          data-testid={`mobile-link-${link.path.slice(1)}`}>
                          {link.label}
                        </button>
                      ))}
                      <div className="h-px bg-[#EBE3DF]/60 my-2" />
                      {PUBLIC_LINKS.map(link => (
                        <button key={link.path} onClick={() => go(link.path)}
                          className={`text-left py-3 px-3 rounded-lg text-sm ${
                            isActive(link.path) ? 'text-[#A0655A] font-medium' : 'text-[#111111]/45'
                          }`}
                          data-testid={`mobile-link-${link.path.slice(1)}`}>
                          {link.label}
                        </button>
                      ))}
                      <div className="h-px bg-[#EBE3DF]/60 my-2" />
                      {user.role === 'admin' && (
                        <button onClick={() => go('/admin')}
                          className={`text-left py-3 px-3 rounded-lg text-sm ${
                            isActive('/admin') ? 'text-[#A0655A] font-medium' : 'text-[#111111]/45'
                          }`}
                          data-testid="mobile-link-admin">
                          Admin
                        </button>
                      )}
                      <button onClick={handleLogout}
                        className="text-left py-3 px-3 text-sm text-[#A0655A]"
                        data-testid="mobile-logout-btn">
                        Logga ut
                      </button>
                    </>
                  ) : (
                    <>
                      {PUBLIC_LINKS.map(link => (
                        <button key={link.path} onClick={() => go(link.path)}
                          className={`text-left py-3 px-3 rounded-lg text-sm transition-colors ${
                            isActive(link.path)
                              ? 'text-[#A0655A] bg-[#A0655A]/[0.06] font-medium'
                              : 'text-[#111111]/65 hover:text-[#A0655A]'
                          }`}
                          data-testid={`mobile-link-${link.path.slice(1)}`}>
                          {link.label}
                        </button>
                      ))}
                      <div className="h-px bg-[#EBE3DF]/60 my-3" />
                      <div className="px-1 space-y-2">
                        {user ? (
                          <>
                            <Button onClick={() => go('/betalning')}
                              className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full w-full"
                              data-testid="mobile-access-btn">
                              Få tillgång <ChevronRight className="ml-1" size={16} />
                            </Button>
                            <button onClick={handleLogout}
                              className="text-sm text-[#111111]/40 py-2 px-3 w-full text-left"
                              data-testid="mobile-logout-btn">
                              Logga ut
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => go('/logga-in')}
                              className="text-sm text-[#111111]/65 py-2 px-3 w-full text-left"
                              data-testid="mobile-login-btn">
                              Logga in
                            </button>
                            <Button onClick={() => go('/registrera')}
                              className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full w-full"
                              data-testid="mobile-register-btn">
                              Kom igång <ChevronRight className="ml-1" size={16} />
                            </Button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};
